import {
  CheckCircleIcon,
  ClipboardDocumentIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import React, { useState, useCallback } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import Filename from '../../../components/Filename';
import SplitPanelBookingLayout from '../../../components/invoice-preview/SplitPanelBookingLayout';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useToast } from '../../../context/ToastContext';
import {
  UploadedReceipt,
  LexOfficeBookingData,
  BookingImportJob,
} from '../../../types';
import BookingDataForm from '../../BulkUploadPage/components/BookingDataForm';
import ImportFailedBanner from '../../BulkUploadPage/components/ImportFailedBanner';

interface LexOfficeBookingModalProps {
  receiptId: string;
  receipt: UploadedReceipt;
  bookingData: LexOfficeBookingData;
  originalBookingData: LexOfficeBookingData | undefined;
  onSave: (data: LexOfficeBookingData) => void;
  onRestore: () => void;
  onClose: () => void;
  onImport: (
    receiptId: string,
    hasChanges: boolean,
    proposedBookingData?: LexOfficeBookingData
  ) => Promise<BookingImportJob | null>;
  onReject: (receiptId: string) => Promise<boolean>;
  isImporting: boolean;
  isRejecting: boolean;
  isImportInProgress: boolean;
  errorMessage?: string;
}

const LexOfficeBookingModal: React.FC<LexOfficeBookingModalProps> = ({
  receiptId,
  receipt,
  bookingData,
  originalBookingData,
  onSave,
  onRestore,
  onClose,
  onImport,
  onReject,
  isImporting,
  isRejecting,
  isImportInProgress,
  errorMessage,
}) => {
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [copiedField, setCopiedField] = useState<'name' | 'id' | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [showImportButton, setShowImportButton] = useState(true);
  const [showRejectButton, setShowRejectButton] = useState(true);

  const dataHasChanges =
    JSON.stringify(bookingData) !== JSON.stringify(originalBookingData);

  const isAlreadyImported = receipt.status === 'imported';
  const isProcessing = isImporting || isRejecting || isImportInProgress;

  const handleCopy = (text: string, field: 'name' | 'id') => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleImport = useCallback(async () => {
    const proposedData = dataHasChanges ? bookingData : undefined;
    const job = await onImport(receiptId, dataHasChanges, proposedData);
    if (job) {
      showToast(
        'Booking Import Started',
        'The booking data is being imported.',
        'success'
      );
      onClose();
    }
  }, [dataHasChanges, bookingData, receiptId, onImport, onClose, showToast]);

  const handleReject = useCallback(async () => {
    const success = await onReject(receiptId);
    if (success) {
      showToast(
        'Booking Rejected',
        'The booking has been rejected. Booking can be generated again for this invoice',
        'success'
      );
      onClose();
    }
  }, [receiptId, onReject, onClose, showToast]);

  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
      role='presentation'
    >
      <button
        className='absolute inset-0 w-full h-full cursor-default'
        onClick={onClose}
        aria-label='Close modal'
        tabIndex={-1}
      />
      <div
        className='bg-white rounded-lg p-8 max-w-7xl max-h-[90vh] overflow-y-auto relative z-10'
        role='dialog'
        aria-modal='true'
      >
        <div className='flex justify-between items-center'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-semibold text-gray-800'>
              Booking Data
            </h3>
            <span className='text-sm text-gray-500'>
              (<Filename
                storageKey={receipt.storageKey}
                fileName={receipt.originalFileName}
              />)
            </span>
            <button
              type='button'
              onClick={() => handleCopy(receipt.originalFileName, 'name')}
              className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
              title='Copy file name'
            >
              {copiedField === 'name' ? (
                <ClipboardDocumentCheckIcon className='w-3.5 h-3.5 text-green-500' />
              ) : (
                <ClipboardDocumentIcon className='w-3.5 h-3.5' />
              )}
            </button>
          </div>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 text-2xl'
          >
            &times;
          </button>
        </div>
        <div className='flex items-center gap-1 mb-4'>
          <span
            className='text-xs italic text-gray-400 font-mono'
            title={receiptId}
          >
            {receiptId}
          </span>
          <button
            type='button'
            onClick={() => handleCopy(receiptId, 'id')}
            className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
            title='Copy receipt ID'
          >
            {copiedField === 'id' ? (
              <ClipboardDocumentCheckIcon className='w-3 h-3 text-green-500' />
            ) : (
              <ClipboardDocumentIcon className='w-3 h-3' />
            )}
          </button>
        </div>
        {receipt.status === 'import-failed' && errorMessage && (
          <div className='mb-4 max-w-2xl'>
            <ImportFailedBanner errorMessage={errorMessage} />
          </div>
        )}
        <SplitPanelBookingLayout receipt={receipt}>
          <BookingDataForm
            bookingData={bookingData}
            onSave={
              !isAlreadyImported && !isImportInProgress ? onSave : undefined
            }
            isEditing={isEditing}
            onEditModeChange={setIsEditing}
            showValidation={showValidation}
            hasChanges={dataHasChanges}
            onRestore={
              !isAlreadyImported && !isImportInProgress ? onRestore : undefined
            }
          />

          {!isAlreadyImported && !isEditing && (
            <div className='flex gap-2 mt-6 pt-4 justify-center'>
              {isProcessing ? (
                <div className='flex items-center gap-2 text-gray-600'>
                  <LoadingSpinner size='sm' />
                  <span className='text-sm'>
                    {isImportInProgress
                      ? 'Importing booking to LexOffice...'
                      : 'Processing...'}
                  </span>
                </div>
              ) : (
                <>
                  {showImportButton && (
                    <ConfirmableButton
                      confirmationMessage='Import this booking?'
                      onConfirm={handleImport}
                      buttonClassName='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium flex items-center gap-2'
                      containerClassName='flex gap-2'
                      onShowConfirmation={show => {
                        setShowRejectButton(!show);
                        const missingCategoryItems =
                          bookingData?.voucherItems.filter(
                            item => !item.categoryId
                          ) || [];
                        if (show && missingCategoryItems.length > 0) {
                          setShowValidation(true);
                        }
                      }}
                      disabled={
                        bookingData?.voucherItems.some(
                          item => !item.categoryId
                        ) || false
                      }
                    >
                      <CheckCircleIcon className='w-5 h-5' />
                      Import
                    </ConfirmableButton>
                  )}
                  {showRejectButton && (
                    <ConfirmableButton
                      confirmationMessage='Reject this booking?'
                      onConfirm={handleReject}
                      buttonClassName='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium flex items-center gap-2'
                      containerClassName='flex gap-2'
                      onShowConfirmation={show => setShowImportButton(!show)}
                    >
                      <XCircleIcon className='w-5 h-5' />
                      Reject
                    </ConfirmableButton>
                  )}
                </>
              )}
            </div>
          )}
        </SplitPanelBookingLayout>
      </div>
    </div>
  );
};

export default LexOfficeBookingModal;
